import { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import useStyles from "./styles";
import { Add, DeleteOutline, RouterOutlined } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";

/** Extrai posição, serial e username do formato "posicao - serial - username" */
function parseOnuItem(onuItem = "") {
  const parts = onuItem?.split(" - ") ?? [];
  return {
    posicao: parts[0]?.trim() ?? "",
    serial: parts[1]?.trim() ?? "",
    username: parts[2]?.trim() ?? "",
  };
}

/** Monta a string onuItem a partir dos 3 campos */
function buildOnuItem({ posicao = "", serial = "", username = "" }) {
  return `${posicao} - ${serial} - ${username}`.trim();
}

/** Valida os campos da ONU */
function validateOnuFields({ posicao, serial }) {
  const errors = { posicao: "", serial: "", general: "" };

  if (posicao && !/^\d+\/\d+(\/\d+)?$/.test(posicao)) {
    errors.posicao = "Formato deve ser 1/1 ou 1/1/1";
  }

  if (!posicao && !serial) {
    errors.general = "Preencha ao menos a posição ou serial";
  }

  return errors;
}

function OnusSelector({
  loading = {},
  editMode = false,
  onusFromErp = [],
  manipulateOnus = [],
  availableHosts = [],
  availableDevices = [],
  setManipulateOnus = () => {},
  manipulateOnuItems = [],
  setManipulateOnuItems = () => {},
  onValidityChange = () => {},
}) {
  const theme = useTheme();
  const styles = useStyles?.(theme) ?? {};
  const [fieldErrors, setFieldErrors] = useState({});
  const [wasEdited, setWasEdited] = useState(false);
  const [alertClosed, setAlertClosed] = useState(false);

  // Reset when editMode changes to false (modal closed)
  useEffect(() => {
    if (!editMode) {
      setWasEdited(false);
      setAlertClosed(false);
    }
  }, [editMode]);

  /** Parse dos campos ao carregar dados iniciais */
  useEffect(() => {
    const errors = {};
    let hasAnyOnu = false;
    (manipulateOnus ?? []).forEach((onu, idx) => {
      const parsed = parseOnuItem(onu?.onuItem);
      if (!onu?.posicao && !onu?.serial && !onu?.username) {
        // Inicializa campos separados se ainda não existem
        const newOnus = [...(manipulateOnus ?? [])];
        newOnus[idx] = { ...newOnus[idx], ...parsed };
        setManipulateOnus?.(newOnus);
      }
      const errs = validateOnuFields(parsed);
      if (errs.posicao || errs.serial || errs.general) {
        errors[idx] = errs;
      }
      if (onu?.onuItem) hasAnyOnu = true;
    });
    setFieldErrors(errors);
    onValidityChange(Object.keys(errors).length === 0);
  }, []);

  /** ADICIONAR / REMOVER ONU */
  const handleAddOnu = () => {
    setWasEdited(true);
    const newOnu = {
      id: `temp-${uuidv4()}`,
      hostId: "",
      groupId: "",
      onuItem: "",
      ipOlt: "",
      posicao: "",
      serial: "",
      username: "",
    };

    setManipulateOnus?.([...(manipulateOnus ?? []), newOnu]);
  };

  const handleRemoveOnu = (index) => {
    setWasEdited(true);
    const newOnus = [...(manipulateOnus ?? [])];
    newOnus.splice(index, 1);
    setManipulateOnus?.(newOnus);

    // Reindexa erros
    const newErrors = {};
    Object.entries(fieldErrors).forEach(([key, val]) => {
      const k = parseInt(key);
      if (k < index) newErrors[k] = val;
      else if (k > index) newErrors[k - 1] = val;
    });
    setFieldErrors(newErrors);
    onValidityChange(Object.keys(newErrors).length === 0);
  };

  /** OBTER OPÇÕES */
  const getAvailableModels = (currentIndex) => {
    const selectedGroupIds = manipulateOnus
      ?.filter((_, idx) => idx !== currentIndex)
      ?.map((onu) => onu?.groupId)
      ?.filter(Boolean);

    return availableDevices?.filter(
      (device) => !selectedGroupIds?.includes(device?.groupid),
    );
  };

  const getAvailableHosts = (currentIndex, selectedGroupId) => {
    if (!selectedGroupId) return [];

    const selectedHostIds = manipulateOnus
      ?.filter((_, idx) => idx !== currentIndex)
      ?.map((onu) => onu?.hostId)
      ?.filter(Boolean);

    return availableHosts?.filter(
      (host) =>
        host?.groups?.some((group) => group?.groupid === selectedGroupId) &&
        !selectedHostIds?.includes(host?.hostid),
    );
  };

  /** SELECIONAR OPÇÕES */
  const handleModelSelect = (index, value) => {
    setWasEdited(true);
    const newOnus = [...(manipulateOnus ?? [])];

    newOnus[index] = {
      ...(newOnus[index] ?? {}),
      groupId: value?.groupid || "",
      hostId: "",
      ipOlt: "",
      onuItem: "",
      posicao: "",
      serial: "",
      username: "",
    };

    setManipulateOnus?.(newOnus);
  };

  const handleHostSelect = (index, selectedHost) => {
    setWasEdited(true);
    const newOnus = [...(manipulateOnus ?? [])];

    if (!selectedHost) {
      newOnus[index] = {
        ...(newOnus[index] ?? {}),
        hostId: "",
        ipOlt: "",
        onuItem: "",
        posicao: "",
        serial: "",
        username: "",
      };
    } else {
      const hostString = selectedHost?.host ?? "";
      const splitedHost = hostString?.split(" - ") ?? [];
      const extractedIp = splitedHost?.[splitedHost.length - 1]?.trim() ?? "";

      newOnus[index] = {
        ...(newOnus[index] ?? {}),
        hostId: selectedHost?.hostid,
        ipOlt: extractedIp || "",
        onuItem: "",
        posicao: "",
        serial: "",
        username: "",
      };
    }

    setManipulateOnus?.(newOnus);
  };

  /** EDITAR CAMPOS DA ONU */
  const handleFieldChange = (index, field, value) => {
    setWasEdited(true);
    let processedValue = value;

    // Aplica mesmas regras de sanitização do modal
    if (field === "posicao") {
      processedValue = value.replace(/[^0-9\/]/g, "");
    } else if (field === "serial") {
      processedValue = value.slice(0, 16);
    }

    const newOnus = [...(manipulateOnus ?? [])];
    const updatedOnu = {
      ...(newOnus[index] ?? {}),
      [field]: processedValue,
    };

    // Reconstroi onuItem
    updatedOnu.onuItem = buildOnuItem({
      posicao: updatedOnu.posicao,
      serial: updatedOnu.serial,
      username: updatedOnu.username,
    });

    newOnus[index] = updatedOnu;
    setManipulateOnus?.(newOnus);

    // Atualiza erros e notifica pai
    const errs = validateOnuFields({
      posicao: updatedOnu.posicao,
      serial: updatedOnu.serial,
    });
    setFieldErrors((prev) => {
      const next = { ...prev, [index]: errs };
      const hasErrors = Object.values(next).some(
        (e) => e.posicao || e.serial || e.general,
      );
      onValidityChange(!hasErrors);
      return next;
    });
  };

  const showInfoAlert = wasEdited && !alertClosed;

  return (
    <Box sx={styles?.container}>
      <Box sx={styles?.boxTitle}>
        <RouterOutlined color="primary" sx={styles?.titleIcon} />
        <Typography variant="mySubtitle2">ONUs:</Typography>
      </Box>

      <Box sx={styles.boxMapOnus}>
        <Collapse in={showInfoAlert}>
          <Alert
            severity="info"
            onClose={() => setAlertClosed(true)}
            sx={{ mb: 1.5, borderRadius: 1 }}
          >
            As coletas de novas ONUs cadastradas só iniciarão após 30 minutos.
          </Alert>
        </Collapse>
        {manipulateOnus?.map?.((onu, index) => {
          const availableModels = getAvailableModels?.(index) ?? [];
          const currentModel = availableDevices?.find(
            (d) => d?.groupid === onu?.groupId,
          );
          const availableHostsForModel =
            getAvailableHosts?.(index, onu?.groupId) ?? [];
          const currentHost = availableHosts?.find(
            (h) =>
              h?.hostid === onu?.hostId ||
              (onu?.ipOlt && h?.host?.includes?.(onu?.ipOlt)),
          );
          const errors = fieldErrors[index] ?? {
            posicao: "",
            serial: "",
            general: "",
          };

          return (
            <Grid container spacing={1} key={onu?.id || index}>
              <Grid item xs={11.6}>
                <Paper
                  variant="outlined"
                  elevation={0}
                  sx={styles.paperOnuItem}
                >
                  <Grid container spacing={1}>
                    {/* Modelo e Host */}
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            size="small"
                            options={availableModels ?? []}
                            value={currentModel ?? null}
                            onChange={(event, newValue) =>
                              handleModelSelect?.(index, newValue)
                            }
                            getOptionLabel={(option) => option?.name ?? ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.groupid === value?.groupid
                            }
                            renderInput={(params) => (
                              <TextField
                                variant="filled"
                                {...params}
                                label="Modelo"
                                placeholder="Selecione o modelo"
                              />
                            )}
                            disabled={
                              (availableModels?.length ?? 0) === 0 ||
                              !!loading.onuFromErp ||
                              !editMode
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            size="small"
                            options={availableHostsForModel ?? []}
                            value={currentHost ?? null}
                            onChange={(event, newValue) =>
                              handleHostSelect?.(index, newValue)
                            }
                            getOptionLabel={(option) => option?.host ?? ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.hostid === value?.hostid
                            }
                            renderInput={(params) => (
                              <TextField
                                variant="filled"
                                {...params}
                                label="Host"
                                placeholder="Selecione o host"
                              />
                            )}
                            disabled={
                              !!loading.onuFromErp ||
                              !onu?.groupId ||
                              (availableHostsForModel?.length ?? 0) === 0 ||
                              !editMode
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {/* Campos da ONU em coluna */}
                    <Grid item xs={12}>
                      <Box sx={styles.onuFieldsRow}>
                        <TextField
                          size="small"
                          variant="filled"
                          label="Posição"
                          value={onu?.posicao ?? ""}
                          onChange={(e) =>
                            handleFieldChange(index, "posicao", e.target.value)
                          }
                          placeholder="Ex: 1/1/1 ou 1/1"
                          disabled={!!loading.onuFromErp || !editMode}
                          error={!!errors.posicao}
                          helperText={errors.posicao}
                          autoComplete="off"
                          sx={styles.onuTextField}
                        />
                        <TextField
                          size="small"
                          variant="filled"
                          label="Serial"
                          value={onu?.serial ?? ""}
                          onChange={(e) =>
                            handleFieldChange(index, "serial", e.target.value)
                          }
                          placeholder="Serial da ONU"
                          disabled={!!loading.onuFromErp || !editMode}
                          error={!!errors.serial}
                          helperText={errors.serial}
                          inputProps={{ maxLength: 16 }}
                          autoComplete="off"
                          sx={styles.onuTextField}
                        />
                        <TextField
                          size="small"
                          variant="filled"
                          label="Username"
                          value={onu?.username ?? ""}
                          onChange={(e) =>
                            handleFieldChange(index, "username", e.target.value)
                          }
                          placeholder="Username"
                          disabled={!!loading.onuFromErp || !editMode}
                          autoComplete="off"
                          sx={styles.onuTextField}
                        />
                        {errors.general && (
                          <Typography color="error" variant="body2">
                            {errors.general}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={0.4} sx={styles?.deleteIcon}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveOnu?.(index)}
                  disabled={!!loading.onuFromErp || !editMode}
                >
                  <DeleteOutline color={editMode ? "error" : "disabled"} />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add color={theme.palette.text.main} />}
          disabled={!!loading.onuFromErp || !editMode}
          onClick={handleAddOnu}
          sx={styles.button}
        >
          Nova ONU
        </Button>
      </Box>
    </Box>
  );
}

export default OnusSelector;
